import React, { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { LmsStudent } from "../../components/course/types";
import { Edit, Eye, Save, Shield, X } from "lucide-react";
import { useToast } from "../../components/ui/use-toast";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import { collection, doc, getDocs, getFirestore, updateDoc } from "firebase/firestore"; // Make sure firebase functions are imported  
import { app } from "../../firebase"; // Adjust the path as necessary  

const ManageLmsAccess = () => {
    const [searchTerm, setSearchTerm] = useState("");
    const [editingId, setEditingId] = useState<string | null>(null);
    const [viewingStudent, setViewingStudent] = useState<any | null>(null);
    const { toast } = useToast();

    const [students, setStudents] = useState<any[]>([]);
    const [editFormData, setEditFormData] = useState<any | null>({
        id: "",
        studentsName: "",
        emailId: "",
        phoneNumber: "",
        isLMSDone: false,
        isIDCardIssued: false,
        isInternal: false,
        mobileNumber: '',
        studentIdNo: ''
    });

    // Pagination states  
    const [currentPage, setCurrentPage] = useState(1);
    const [courses, setCourses] = useState<any[]>([]);
    const recordsPerPage = 15;

    const db = getFirestore(app); // Initialize Firestore  

    // Fetch students and courses data from Firestore  
    useEffect(() => {
        const fetchData = async () => {
            try {
                const studentsCollection = collection(db, "students");
                const coursesCollection = collection(db, "courses");

                // Fetch students  
                const studentSnapshot = await getDocs(studentsCollection);
                const fetchedStudents = studentSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    ...doc.data(),
                })) as any[];

                // Fetch courses  
                const courseSnapshot = await getDocs(coursesCollection);
                const fetchedCourses = courseSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    courseName: doc.data().courseName,
                }));

                setCourses(fetchedCourses);
                // Map through students and match with courseNames  
                const updatedStudents = fetchedStudents.map(student => {
                    const courseNames = student?.courses?.map((instance:any) => {
                        const course = fetchedCourses.find(c => c.id == instance.courseId);
                        return course ? course.courseName : "Unknown Course"; // Handle missing courseId  
                    });

                    return {
                        ...student,
                        coursesAssigned: courseNames.join(", ") // Join course names into a single string  
                    };
                }).filter(x => x.studentIdNo);

                setStudents(updatedStudents);
            } catch (error) {
                console.error("Error fetching students or courses:", error);
                toast({
                    title: "Error",
                    description: "There was an issue fetching student data.",
                });
            }
        };

        fetchData();
    }, [db, toast]); // Dependencies for useEffect   

    // Get current records for the displayed page  
    const indexOfLastRecord = currentPage * recordsPerPage;
    const indexOfFirstRecord = indexOfLastRecord - recordsPerPage;
    

    const totalPages = Math.ceil(students.length / recordsPerPage);

    const filteredStudents = students.filter(
        (student) =>
            student?.studentsName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            student?.emailId?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
            student?.studentIdNo?.toLowerCase().includes(searchTerm?.toLowerCase())
    );

    const currentRecords = filteredStudents.slice(indexOfFirstRecord, indexOfLastRecord);

    const handleEditClick = (student: LmsStudent) => {
        setEditingId(student.id);
        setEditFormData({ ...student });
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setEditFormData(null);
    };

    const handleSaveEdit = () => {
        if (!editFormData) return;

        setStudents(students.map(student => student.id === editingId ? editFormData : student));
        setEditingId(null);
        setEditFormData(null);

        toast({
            title: "Student updated",
            description: "The student's LMS access details have been successfully updated.",
        });
    };

    const handleFormChange = (field: any, value: any) => {
        if (editFormData) {
            setEditFormData({ ...editFormData, [field]: value });
        }
    };

    const handleToggleAccess = (id: string) => {
        setStudents(students.map(student => {
            if (student.id === id) {
                return { ...student, isLMSDone: !student.isLMSDone };
            }
            return student;
        }));

        const targetStudent = students.find(s => s.id === id);
        const body = {
            id:id,
            ...targetStudent,
            isLMSDone: !targetStudent?.isLMSDone
        };
        updateStudent(body);

        toast({
            title: targetStudent?.isLMSDone ? "Access Revoked" : "Access Granted",
            description: `LMS access has been ${targetStudent?.isLMSDone ? 'revoked from' : 'granted to'} ${targetStudent?.studentsName}.`,
        });
    };

    const updateStudent = async (student: any) => {
        await updateDoc(doc(db, "students", student?.id), student);

    }
    const handleViewDetails = (student: any) => {
        setViewingStudent(student);
    };

    // Pagination Handlers  
    const nextPage = () => {
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };

    const prevPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    return (
        <div className="container py-6 space-y-6">
            <div className="flex justify-between items-center">
                <h1 className="text -2xl font-bold">Manage LMS Access</h1>
                <div className="flex space-x-2">
                    <Button variant="outline"><Eye className="mr-2" /> Export List</Button>
                    <Button><Shield className="mr-2" /> Grant New Access</Button>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Student LMS Access</CardTitle>
                    <CardDescription>
                        Manage student access to the Learning Management System
                    </CardDescription>
                    <div className="mt-4">
                        <Input
                            placeholder="Search by name, email, or ID..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="max-w-md"
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>ID</TableHead>
                                <TableHead>Name</TableHead>
                                <TableHead>Email</TableHead>
                                <TableHead>Courses</TableHead>
                                <TableHead>LMS Status</TableHead>
                                <TableHead>Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {currentRecords.map((student) => (
                                <TableRow key={student.id}>
                                    <TableCell className="font-medium">
                                        {editingId === student.id ? (
                                            <Input
                                                value={editFormData?.studentIdNo}
                                                onChange={(e) => handleFormChange('studentIdNo', e.target.value)}
                                                className="max-w-[120px]"
                                                readOnly
                                            />
                                        ) : (
                                            student.studentIdNo
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === student.id ? (
                                            <Input
                                                value={editFormData?.studentsName}
                                                onChange={(e) => handleFormChange('studentsName', e.target.value)}
                                                className="max-w-[200px]"
                                            />
                                        ) : (
                                            student.studentsName
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === student.id ? (
                                            <Input
                                                value={editFormData?.emailId}
                                                onChange={(e) => handleFormChange('emailId', e.target.value)}
                                                className="max-w-[200px]"
                                            />
                                        ) : (
                                            student.emailId
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {editingId === student.id ? (
                                            <Input
                                                value={editFormData?.coursesAssigned}
                                                onChange={(e) => handleFormChange('coursesAssigned', e.target.value)}
                                                className="max-w-[200px]"
                                            />
                                        ) : (
                                            student.coursesAssigned
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {student.isLMSDone ? (
                                            <Badge variant="default" className="bg-green-500">Active</Badge>
                                        ) : (
                                            <Badge variant="destructive">Inactive</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <div className="flex space-x-2">
                                            {editingId === student.id ? (
                                                <>
                                                    <Button variant="outline" size="sm" onClick={handleCancelEdit}><X size={16} className="mr-1" /> Cancel</Button>
                                                    <Button variant="default" size="sm" onClick={handleSaveEdit}><Save size={16} className="mr-1" /> Save</Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Dialog>
                                                        <DialogTrigger asChild>
                                                            <Button variant="outline" size="sm" onClick={() => handleViewDetails(student)}><Eye size={16} className="mr-1" /> View</Button>
                                                        </DialogTrigger>
                                                        <DialogContent className="sm:max-w-md">
                                                            <DialogHeader>
                                                                <DialogTitle>Student Details</DialogTitle>
                                                                <DialogDescription>
                                                                    Detailed information about {viewingStudent?.name}
                                                                </DialogDescription>
                                                            </DialogHeader>
                                                            <div className="grid gap-4 py-4">
                                                                <div className="grid grid-cols-4 items-center gap-4">
                                                                    <span className="font-semibold text-right">ID:</span>
                                                                    <span className="col-span-3">{viewingStudent?.idNumber}</span>
                                                                </div>
                                                                <div className="grid grid-cols-4 items-center gap-4">
                                                                    <span className="font-semibold text-right">Name:</span>
                                                                    <span className="col-span-3">{viewingStudent?.name}</span>
                                                                </div>
                                                                <div className="grid grid-cols-4 items-center gap-4">
                                                                    <span className="font-semibold text-right">Email:</span>
                                                                    <span className="col-span-3">{viewingStudent?.emailId}</span>
                                                                </div>
                                                                <div className="grid grid-cols-4 items-center gap-4">
                                                                    <span className="font-semibold text-right">Courses:</span>
                                                                    <span className="col-span-3">{viewingStudent?.coursesAssigned}</span>
                                                                </div>
                                                                <div className="grid grid-cols-4 items-center gap-4">
                                                                    <span className="font-semibold text-right">LMS Access:</span>
                                                                    <span className="col-span-3">
                                                                        {viewingStudent?.isLMSDone ? (
                                                                            <Badge variant="default" className="bg-green-500">Active</Badge>
                                                                        ) : (
                                                                            <Badge variant="destructive">Inactive</Badge>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                                <div className="grid grid-cols-4 items-center gap-4">
                                                                    <span className="font-semibold text-right">ID Card:</span>
                                                                    <span className="col-span-3">
                                                                        {viewingStudent?.idCardIssued ? (
                                                                            <Badge variant="outline" className="bg-gray-100">Issued</Badge>
                                                                        ) : (
                                                                            <Badge variant="outline" className="bg-yellow-100">Pending</Badge>
                                                                        )}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </DialogContent>
                                                    </Dialog>
                                                    {student.isLMSDone ? (
                                                        <Button variant="destructive" size="sm" onClick={() => handleToggleAccess(student.id)}>Revoke</Button>
                                                    ) : (
                                                        <Button size="sm" onClick={() => handleToggleAccess(student.id)}>Grant</Button>
                                                    )}
                                                </>
                                            )}
                                        </div>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </CardContent>
            </Card>

            {/* Pagination Controls */}
            <div className="flex justify-between mt-4 px-4">
                <Button onClick={prevPage} disabled={currentPage === 1} variant="outline">Previous</Button>
                <span>{`Page ${currentPage} of ${totalPages}`}</span>
                <Button onClick={nextPage} disabled={currentPage === totalPages} variant="outline">Next</Button>
            </div>
        </div>
    );
};

export default ManageLmsAccess;  